import React from 'react'
import { v4 as uuidV4 } from 'uuid'
import * as yup from 'yup'
import { FormProvider, useFieldArray } from 'react-hook-form'
import { ObjectSchema } from 'yup'
import { SiteConfiguration } from '@matech/thebigpos-sdk'
import { useIntl } from 'react-intl'
import {
	AutoTaskRemindersFormFieldValues,
	Reminder,
} from '@/forms/AutoTaskRemindersForm/types'
import AdditionalRemindersList from './AdditionalRemindersList'
import ZeroDayReminderSwitch from './ZeroDayReminderSwitch'
import FormRow from '../../components/formControls/FormRow'
import useForm from '../../hooks/useForm'
import LoadingButton from '../../components/Button/LoadingButton'

export type AutoTaskRemindersFormProps = {
	autoTaskReminderIntervalsInDays?: SiteConfiguration['autoTaskReminderIntervalsInDays']
	onSubmit: (
		data: SiteConfiguration['autoTaskReminderIntervalsInDays']
	) => void
	loading?: boolean
}

const getReminderValueFromForm = (
	days: number,
	sequence: 'before' | 'after'
) => {
	const isAfter = sequence === 'after'
	const multiplier = isAfter ? -1 : 1

	return days * multiplier
}

const AutoTaskRemindersForm = ({
	autoTaskReminderIntervalsInDays = [],
	onSubmit,
	loading,
}: AutoTaskRemindersFormProps) => {
	const { formatMessage } = useIntl()

	const schema: ObjectSchema<AutoTaskRemindersFormFieldValues> = yup
		.object()
		.shape({
			zeroDayReminderEnabled: yup.boolean().required(),
			reminders: yup.array().of(
				yup
					.object()
					.shape({
						id: yup.string().required(),
						days: yup
							.number()
							.min(
								1,
								formatMessage({
									id: 'auto_task_reminders.validation.days.min',
								})
							)
							.required(
								formatMessage({
									id: 'auto_task_reminders.validation.days.required',
								})
							),
						sequence: yup
							.string()
							.oneOf(['before', 'after'], {
								message: formatMessage({
									id: 'auto_task_reminders.validation.sequence.oneOf',
								}),
							})
							.required(
								formatMessage({
									id: 'auto_task_reminders.validation.sequence.required',
								})
							),
					})
					.test(
						'duplicate',
						formatMessage({
							id: 'auto_task_reminders.validation.duplicate',
						}),
						(reminder, context) => {
							return !context.parent.some(
								(r: Reminder) =>
									r.id !== reminder.id &&
									r.days === reminder.days &&
									r.sequence === reminder.sequence
							)
						}
					)
			),
		})

	const methods = useForm<AutoTaskRemindersFormFieldValues>({
		schema,
		defaultValues: {
			zeroDayReminderEnabled:
				autoTaskReminderIntervalsInDays?.includes(0),
			reminders:
				autoTaskReminderIntervalsInDays
					?.filter((v) => v !== 0)
					.sort((a: number, b: number) => a - b)
					.reverse()
					.map((value: number) => ({
						id: uuidV4(),
						days: Math.abs(value),
						sequence: value < 0 ? 'after' : 'before',
					})) || [],
		},
		mode: 'onChange',
		reValidateMode: 'onChange',
	})

	const { control, handleSubmit: handleFormSubmit } = methods

	const {
		fields: items,
		append,
		remove,
	} = useFieldArray({
		control,
		name: 'reminders',
	})

	const handleSubmit = (
		formValues: AutoTaskRemindersFormFieldValues
	) => {
		const zeroDayReminderInDays = formValues.zeroDayReminderEnabled
			? [0]
			: []
		const autoTaskReminderIntervalsInDays = formValues.reminders
			? formValues.reminders.map((reminder) => {
					return getReminderValueFromForm(
						reminder.days ?? 0,
						reminder.sequence
					)
				})
			: []

		const filteredIntervals = autoTaskReminderIntervalsInDays.filter(
			(interval) => interval !== 0
		)

		onSubmit([...zeroDayReminderInDays, ...filteredIntervals])
	}

	return (
		<FormProvider {...methods}>
			<div className="flex flex-col w-full mt-4">
				<FormRow>
					<ZeroDayReminderSwitch />
				</FormRow>
				<AdditionalRemindersList
					reminders={items}
					onAddReminder={append}
					onRemoveReminder={remove}
				/>
				<LoadingButton
					text={formatMessage({
						id: 'auto_task_reminders.submit_reminders',
					})}
					variant="contained"
					onClick={handleFormSubmit(handleSubmit)}
					loading={loading}
				/>
			</div>
		</FormProvider>
	)
}

export default AutoTaskRemindersForm
